import classNames from "classnames";
import { observer } from "mobx-react";
import React from "react";
import { Avatar } from "@fidget/common/components/avatar/avatar";
import { FuzzyTime } from "@fidget/common/components/chat/FuzzyTime";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { ChatItemGroup, ChatItemStatus } from "../../models/room/chat";
import { IChatItem } from "../../models/room/chat/ChatItem";
import styles from "./chatSection.css";

interface Props {
  item: ChatItemGroup;
  itemRenderer: (item: IChatItem) => JSX.Element;
  onUndeliveredClick?: (id: string) => void;
}

export const ChatSection = observer((props: Props) => {
  const getMessageStatusLabel = (status: ChatItemStatus): string => {
    switch (status) {
      case ChatItemStatus.SENDING:
        return intl.formatMessage("conversation.messageStatus.sending");
      case ChatItemStatus.SENT:
        return intl.formatMessage("conversation.messageStatus.sent");
      case ChatItemStatus.DELIVERED:
        return intl.formatMessage("conversation.messageStatus.delivered");
      case ChatItemStatus.READ:
        return intl.formatMessage("conversation.messageStatus.read");
      default:
        throw new Error("unsupported message status: " + status);
    }
  };
  const render = () => {
    const { item } = props;
    const alignRight = item.user && item.user.isAgent;
    const tooltipTransform = alignRight ? "translateX(-77%)" : "translateX(-35%)";
    const tooltipOutsetTransform = alignRight ? "translateX(250%)" : "translateX(-50%)";

    return (
      <div
        className={classNames(styles.section, styles.dashboard, {
          [styles["align-right"]]: alignRight
        })}
      >
        <div className={styles.content}>
          <TooltipComponent
            positionPreset={"top"}
            text={item.user.displayedName}
            maxWidth={"80px"}
            breakWords
            changeTransformTooltip={tooltipTransform}
            changeTransformOutset={tooltipOutsetTransform}
          >
            <div className={styles.avatar}>
              <Avatar user={item.user} />
            </div>
          </TooltipComponent>
          <div className={styles.space} />
          <div className={styles.items}>
            {renderSender()}
            {renderBody()}
          </div>
        </div>
        {renderTimeAndStatus(item)}
      </div>
    );
  };

  const renderTimeAndStatus = (item: ChatItemGroup) => {
    if (item.status === ChatItemStatus.UNDELIVERED) {
      const { onUndeliveredClick } = props;
      const onClick = onUndeliveredClick ? () => onUndeliveredClick(item.id) : undefined;
      return (
        <div
          className={classNames(styles["time-and-status"], styles["status-error"])}
          onClick={onClick}
        >
          <i className="icon-exclamation-mark" />
          {intl.formatMessage("conversation.messageStatus.undelivered")}
        </div>
      );
    } else {
      const statusLabel = item.status && getMessageStatusLabel(item.status);
      const { isCurrent = false } = item.user as { isCurrent?: boolean };

      return (
        <div className={styles["time-and-status"]}>
          <FuzzyTime timestamp={item.timestamp} />
          {isCurrent && <span className={styles.status}>{statusLabel}</span>}
        </div>
      );
    }
  };

  const renderBody = (): React.ReactNode => {
    const { item, itemRenderer } = props;
    return item.sortedItems.map((subitem) => itemRenderer(subitem));
  };

  const renderSender = () => {
    const { isBot = false } = props.item.user as { isBot?: boolean };
    return (
      <div className={classNames(styles.sender, { [styles.lekta]: isBot })}>
        {props.item.user.displayedName}
      </div>
    );
  };

  return render();
});
